import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContent } from '../../context/ContentContext';

type NavItem = { label: string; action: () => void };

function useFooterData() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useContent();

  const navigateToSection = (path: string, id: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'instant' });
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
    }, 300);
  };

  const scrollToSection = (id: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.getElementById(id);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
    }
  };

  const navItems: NavItem[] = [
    { label: 'Zur Startseite',         action: () => navigate('/') },
    { label: 'Zimmer',                 action: () => { navigate('/zimmer'); window.scrollTo({ top: 0, behavior: 'instant' }); } },
    { label: 'Das Hotel',              action: () => { navigate('/hotel'); window.scrollTo({ top: 0, behavior: 'instant' }); } },
    { label: 'Lage & Umgebung',        action: () => scrollToSection('lage') },
    { label: 'Kontakt & Anreise',      action: () => scrollToSection('kontakt') },
    { label: 'Ausflugsziele',          action: () => { navigate('/ausflugsziele'); window.scrollTo({ top: 0, behavior: 'instant' }); } },
    { label: 'Arbeiten im Alpenhof',   action: () => navigateToSection('/hotel', 'karriere') },
  ];

  const legalLinks = [
    { label: 'Impressum', path: '/impressum' },
    { label: 'Datenschutz', path: '/datenschutz' },
    { label: 'AGB', path: '/agb' },
  ];

  return { navItems, legalLinks, navigate, scrollToSection, t };
}

/* ============================================================ */
/* VARIANTE 1: Dekorativer Bild-Streifen + heller Cremeton       */
/* ============================================================ */
function FooterVariant1() {
  const { navItems, legalLinks, navigate, scrollToSection, t } = useFooterData();

  return (
    <footer className="relative bg-stone-50">
      {/* Deko-Bildstreifen */}
      <div
        className="h-56 sm:h-64 bg-cover bg-center"
        style={{ backgroundImage: "url('/images/ausflugsziele/Ausflug_Ammersee_Steg.jpg')" }}
        aria-hidden="true"
      />
      <div className="h-[2px] bg-gradient-to-r from-stone-50 via-amber-700/60 to-stone-50" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-12 sm:pb-16">
        <div className="flex items-center gap-6 sm:gap-8 mb-12 sm:mb-16">
          <img src="/images/brand/logo_hotel_alpenhof.png" alt="Hotel Alpenhof Gauting Logo" className="h-20 sm:h-28 w-auto shrink-0" />
          <div className="border-l-2 border-amber-700/40 pl-6 sm:pl-8">
            <p className="text-lg sm:text-2xl font-serif text-stone-800 leading-snug mb-1">{t('footerTagline')}</p>
            <p className="text-[11px] text-stone-500 tracking-[0.25em] uppercase">Hotel Alpenhof Gauting</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-stone-900/5 shadow-sm">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-5">Navigation</h3>
            <ul className="space-y-2.5">
              {navItems.map(({ label, action }) => (
                <li key={label}>
                  <button onClick={action} className="text-sm text-stone-600 hover:text-amber-700 transition-colors cursor-pointer block">{label}</button>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-stone-900/5 shadow-sm">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-5">Rezeption</h3>
            <div className="space-y-4 text-sm text-stone-600 mb-8">
              <div className="flex items-baseline justify-between">
                <span className="text-stone-800 font-medium">Mo – Fr</span>
                <span>7 – 12 & 15 – 22 Uhr</span>
              </div>
              <div className="h-px bg-stone-900/10" />
              <div className="flex items-baseline justify-between">
                <span className="text-stone-800 font-medium">Sa, So & Feiertage</span>
                <span>8 – 12 & 15 – 22 Uhr</span>
              </div>
            </div>
            <button
              onClick={() => scrollToSection('kontakt')}
              className="w-full inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm font-medium py-3 rounded-xl transition-all duration-200 cursor-pointer"
            >
              <i className="ri-calendar-check-line text-base" />
              Zimmer anfragen
            </button>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-stone-900/5 shadow-sm">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-5">Kontakt</h3>
            <div className="space-y-4 text-sm text-stone-600">
              <p className="whitespace-pre-line leading-relaxed text-stone-700">{t('footerAddress')}</p>
              <div className="h-px bg-stone-900/10" />
              <div className="space-y-2">
                <a href="tel:+498985642910" className="flex items-center gap-3 text-stone-700 hover:text-amber-700 transition-colors">
                  <i className="ri-phone-line text-amber-700 text-base"></i>
                  {t('footerPhone')}
                </a>
                <a href="mailto:hotel@alpenhof-gauting.de" className="flex items-center gap-3 text-stone-700 hover:text-amber-700 transition-colors">
                  <i className="ri-mail-line text-amber-700 text-base"></i>
                  hotel@alpenhof-gauting.de
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-stone-900/10 bg-white/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
            <p className="text-xs text-stone-500 text-center sm:text-left">{t('footerCopyright')}</p>
            <div className="flex items-center gap-1">
              {legalLinks.map((item, i, arr) => (
                <span key={item.label} className="flex items-center gap-1">
                  <button onClick={() => { navigate(item.path); window.scrollTo({ top: 0, behavior: 'instant' }); }} className="text-xs text-stone-500 hover:text-amber-700 transition-colors cursor-pointer whitespace-nowrap">{item.label}</button>
                  {i < arr.length - 1 && <span className="text-stone-400 text-xs">·</span>}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ============================================================ */
/* VARIANTE 2: Cremeton-Footer komplett ohne Bild                */
/* ============================================================ */
function FooterVariant2() {
  const { navItems, legalLinks, navigate, scrollToSection, t } = useFooterData();

  return (
    <footer className="relative bg-amber-50/40">
      <div className="h-[3px] bg-gradient-to-r from-transparent via-amber-700 to-transparent" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-12 sm:pb-16">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-14 sm:mb-20">
          <div className="flex items-center gap-6 sm:gap-8">
            <img src="/images/brand/logo_hotel_alpenhof.png" alt="Hotel Alpenhof Gauting Logo" className="h-20 sm:h-28 w-auto shrink-0" />
            <div className="border-l-2 border-amber-700/40 pl-6 sm:pl-8">
              <p className="text-lg sm:text-2xl font-serif text-stone-800 leading-snug mb-1">{t('footerTagline')}</p>
              <p className="text-[11px] text-stone-500 tracking-[0.25em] uppercase">Hotel Alpenhof Gauting</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-amber-700/70">
            <i className="ri-mountain-line text-2xl" />
            <i className="ri-leaf-line text-2xl" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12">
          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-5">Navigation</h3>
            <ul className="space-y-2.5">
              {navItems.map(({ label, action }) => (
                <li key={label}>
                  <button onClick={action} className="text-sm text-stone-600 hover:text-amber-700 transition-colors cursor-pointer block">{label}</button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-5">Rezeption</h3>
            <div className="space-y-4 text-sm text-stone-600 mb-8">
              <div className="flex items-baseline justify-between">
                <span className="text-stone-800 font-medium">Mo – Fr</span>
                <span>7 – 12 & 15 – 22 Uhr</span>
              </div>
              <div className="h-px bg-stone-900/10" />
              <div className="flex items-baseline justify-between">
                <span className="text-stone-800 font-medium">Sa, So & Feiertage</span>
                <span>8 – 12 & 15 – 22 Uhr</span>
              </div>
            </div>
            <button
              onClick={() => scrollToSection('kontakt')}
              className="inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-all duration-200 cursor-pointer"
            >
              <i className="ri-calendar-check-line text-base" />
              Zimmer anfragen
            </button>
          </div>

          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-5">Kontakt</h3>
            <div className="space-y-4 text-sm text-stone-600">
              <p className="whitespace-pre-line leading-relaxed text-stone-700">{t('footerAddress')}</p>
              <div className="h-px bg-stone-900/10" />
              <div className="space-y-2">
                <a href="tel:+498985642910" className="flex items-center gap-3 text-stone-700 hover:text-amber-700 transition-colors">
                  <i className="ri-phone-line text-amber-700 text-base"></i>
                  {t('footerPhone')}
                </a>
                <a href="mailto:hotel@alpenhof-gauting.de" className="flex items-center gap-3 text-stone-700 hover:text-amber-700 transition-colors">
                  <i className="ri-mail-line text-amber-700 text-base"></i>
                  hotel@alpenhof-gauting.de
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-stone-900/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
            <p className="text-xs text-stone-500 text-center sm:text-left">{t('footerCopyright')}</p>
            <div className="flex items-center gap-1">
              {legalLinks.map((item, i, arr) => (
                <span key={item.label} className="flex items-center gap-1">
                  <button onClick={() => { navigate(item.path); window.scrollTo({ top: 0, behavior: 'instant' }); }} className="text-xs text-stone-500 hover:text-amber-700 transition-colors cursor-pointer whitespace-nowrap">{item.label}</button>
                  {i < arr.length - 1 && <span className="text-stone-400 text-xs">·</span>}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ============================================================ */
/* VARIANTE 3: Bild mit weißem Overlay (aquarell-artig)          */
/* ============================================================ */
function FooterVariant3() {
  const { navItems, legalLinks, navigate, scrollToSection, t } = useFooterData();

  return (
    <footer className="relative text-stone-800 bg-stone-50">
      <div
        className="absolute inset-0 bg-cover bg-top"
        style={{ backgroundImage: "url('/images/ausflugsziele/Ausflug_Ammersee_Steg.jpg')" }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-white/60" aria-hidden="true" />

      <div className="relative h-[2px] bg-gradient-to-r from-white via-amber-700/60 to-white" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-12 sm:pb-16">
        <div className="inline-flex items-center gap-6 sm:gap-8 mb-14 sm:mb-20 bg-white/80 backdrop-blur-sm rounded-2xl border border-stone-900/10 px-6 sm:px-8 py-5 sm:py-6">
          <img src="/images/brand/logo_hotel_alpenhof.png" alt="Hotel Alpenhof Gauting Logo" className="h-20 sm:h-28 w-auto shrink-0" />
          <div className="border-l-2 border-amber-700/40 pl-6 sm:pl-8">
            <p className="text-lg sm:text-2xl font-serif text-stone-800 leading-snug mb-1">{t('footerTagline')}</p>
            <p className="text-[11px] text-stone-500 tracking-[0.25em] uppercase">Hotel Alpenhof Gauting</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
          <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-stone-900/10 shadow-sm">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-5">Navigation</h3>
            <ul className="space-y-2.5">
              {navItems.map(({ label, action }) => (
                <li key={label}>
                  <button onClick={action} className="text-sm text-stone-600 hover:text-amber-700 transition-colors cursor-pointer block">{label}</button>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-stone-900/10 shadow-sm">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-5">Rezeption</h3>
            <div className="space-y-4 text-sm text-stone-600 mb-8">
              <div className="flex items-baseline justify-between">
                <span className="text-stone-800 font-medium">Mo – Fr</span>
                <span>7 – 12 & 15 – 22 Uhr</span>
              </div>
              <div className="h-px bg-stone-900/10" />
              <div className="flex items-baseline justify-between">
                <span className="text-stone-800 font-medium">Sa, So & Feiertage</span>
                <span>8 – 12 & 15 – 22 Uhr</span>
              </div>
            </div>
            <button
              onClick={() => scrollToSection('kontakt')}
              className="w-full inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm font-medium py-3 rounded-xl transition-all duration-200 cursor-pointer"
            >
              <i className="ri-calendar-check-line text-base" />
              Zimmer anfragen
            </button>
          </div>

          <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-stone-900/10 shadow-sm">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-5">Kontakt</h3>
            <div className="space-y-4 text-sm text-stone-600">
              <p className="whitespace-pre-line leading-relaxed text-stone-700">{t('footerAddress')}</p>
              <div className="h-px bg-stone-900/10" />
              <div className="space-y-2">
                <a href="tel:+498985642910" className="flex items-center gap-3 text-stone-700 hover:text-amber-700 transition-colors">
                  <i className="ri-phone-line text-amber-700 text-base"></i>
                  {t('footerPhone')}
                </a>
                <a href="mailto:hotel@alpenhof-gauting.de" className="flex items-center gap-3 text-stone-700 hover:text-amber-700 transition-colors">
                  <i className="ri-mail-line text-amber-700 text-base"></i>
                  hotel@alpenhof-gauting.de
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative border-t border-stone-900/10 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
            <p className="text-xs text-stone-600 text-center sm:text-left">{t('footerCopyright')}</p>
            <div className="flex items-center gap-1">
              {legalLinks.map((item, i, arr) => (
                <span key={item.label} className="flex items-center gap-1">
                  <button onClick={() => { navigate(item.path); window.scrollTo({ top: 0, behavior: 'instant' }); }} className="text-xs text-stone-600 hover:text-amber-700 transition-colors cursor-pointer whitespace-nowrap">{item.label}</button>
                  {i < arr.length - 1 && <span className="text-stone-400 text-xs">·</span>}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ============================================================ */
/* VARIANTE 4: Amber-Brand-Statement (wie CtaBanner-Erweiterung) */
/* ============================================================ */
function FooterVariant4() {
  const { navItems, legalLinks, navigate, scrollToSection, t } = useFooterData();

  return (
    <footer className="relative bg-amber-700 text-gray-800 overflow-hidden">
      <div className="absolute inset-0 opacity-20 pointer-events-none"
           style={{ background: 'radial-gradient(ellipse at top, rgba(255,255,255,0.15), transparent 60%)' }}
           aria-hidden="true" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-28 pb-12 sm:pb-16">
        <div className="mb-16 sm:mb-20">
          <p className="text-[10px] sm:text-xs font-semibold tracking-wider sm:tracking-widest uppercase text-gray-700 mb-4">
            Zu Gast im Alpenhof
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif text-gray-800 mb-6 leading-tight whitespace-nowrap">
            {t('footerTagline').replace(/^["„»"']+|["„»"'"]+$/g, '')}
          </h2>
          <div className="h-px w-24 bg-gray-800/30" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-12">
          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-800 mb-5">Navigation</h3>
            <ul className="space-y-2.5">
              {navItems.map(({ label, action }) => (
                <li key={label}>
                  <button onClick={action} className="text-sm text-gray-600 hover:text-gray-800 transition-colors cursor-pointer block">{label}</button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-800 mb-5">Rezeption</h3>
            <div className="space-y-4 text-sm text-gray-600 mb-8">
              <div className="flex items-baseline justify-between">
                <span className="text-gray-800 font-semibold">Mo – Fr</span>
                <span>7 – 12 & 15 – 22 Uhr</span>
              </div>
              <div className="h-px bg-gray-800/15" />
              <div className="flex items-baseline justify-between">
                <span className="text-gray-800 font-semibold">Sa, So & Feiertage</span>
                <span>8 – 12 & 15 – 22 Uhr</span>
              </div>
            </div>
            <button
              onClick={() => scrollToSection('kontakt')}
              className="inline-flex items-center justify-center gap-2 bg-gray-800 text-amber-50 hover:bg-gray-700 text-sm font-semibold px-6 py-3 rounded-md transition-all duration-200 cursor-pointer shadow-lg"
            >
              <i className="ri-calendar-check-line text-base" />
              Zimmer anfragen
            </button>
          </div>

          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-800 mb-5">Kontakt</h3>
            <div className="space-y-4 text-sm text-gray-600">
              <p className="whitespace-pre-line leading-relaxed text-gray-800">{t('footerAddress')}</p>
              <div className="h-px bg-gray-800/15" />
              <div className="space-y-2">
                <a href="tel:+498985642910" className="flex items-center gap-3 text-gray-800 hover:text-gray-600 transition-colors">
                  <i className="ri-phone-line text-gray-800 text-base"></i>
                  {t('footerPhone')}
                </a>
                <a href="mailto:hotel@alpenhof-gauting.de" className="flex items-center gap-3 text-gray-800 hover:text-gray-600 transition-colors">
                  <i className="ri-mail-line text-gray-800 text-base"></i>
                  hotel@alpenhof-gauting.de
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative bg-amber-800/40 border-t border-gray-800/15">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
            <p className="text-xs text-gray-700 text-center sm:text-left">{t('footerCopyright')}</p>
            <div className="flex items-center gap-1">
              {legalLinks.map((item, i, arr) => (
                <span key={item.label} className="flex items-center gap-1">
                  <button onClick={() => { navigate(item.path); window.scrollTo({ top: 0, behavior: 'instant' }); }} className="text-xs text-gray-700 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap">{item.label}</button>
                  {i < arr.length - 1 && <span className="text-gray-600 text-xs">·</span>}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ============================================================ */
/* VARIANTE 5: Editorial mit Ausflugs-Bildcollage                */
/* ============================================================ */
function FooterVariant5() {
  const { navItems, legalLinks, navigate, scrollToSection, t } = useFooterData();

  const collage = [
    { src: '/images/ausflugsziele/Ausflug_Ammersee_Steg.jpg', label: 'Ammersee' },
    { src: '/images/ausflugsziele/Ausflug_Andechs_Fruehlingswiese.jpg', label: 'Andechs' },
    { src: '/images/ausflugsziele/Ausflug_Starnberger_See_Tutzing_Midgardhaus.jpg', label: 'Starnberger See' },
    { src: '/images/ausflugsziele/Ausflug_Gauting_Wuermtal (2).jpg', label: 'Würmtal' },
  ];

  return (
    <footer className="relative bg-stone-50">
      <div className="h-[2px] bg-gradient-to-r from-stone-50 via-amber-700/60 to-stone-50" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-12 sm:pb-16">
        {/* Intro-Zeile */}
        <div className="text-center mb-12 sm:mb-16">
          <p className="text-[10px] sm:text-xs font-semibold tracking-widest uppercase text-amber-700 mb-3">Alpenhof Gauting</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif text-stone-800 leading-tight">
            {t('footerTagline')}
          </h2>
        </div>

        {/* Bildcollage als "Ausflüge ringsum" */}
        <div className="mb-16 sm:mb-20">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-amber-700">Ausflüge ringsum</h3>
            <button
              onClick={() => { navigate('/ausflugsziele'); window.scrollTo({ top: 0, behavior: 'instant' }); }}
              className="text-xs text-stone-600 hover:text-amber-700 transition-colors cursor-pointer inline-flex items-center gap-1"
            >
              Alle Ausflugsziele
              <i className="ri-arrow-right-line" />
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {collage.map((item) => (
              <button
                key={item.src}
                onClick={() => { navigate('/ausflugsziele'); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                className="group relative aspect-[4/5] rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <img
                  src={item.src}
                  alt={item.label}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <span className="absolute bottom-3 left-3 text-xs font-medium text-white tracking-wide">{item.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Drei Spalten mit Divider */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-12 pt-12 border-t border-stone-900/10">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <img src="/images/brand/logo_stick.webp" alt="Hotel Alpenhof" className="h-12 w-auto" />
              <div>
                <p className="text-xs font-semibold text-stone-800">Hotel Alpenhof</p>
                <p className="text-[10px] text-stone-500 tracking-wider uppercase">Gauting</p>
              </div>
            </div>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-4">Navigation</h3>
            <ul className="space-y-2">
              {navItems.map(({ label, action }) => (
                <li key={label}>
                  <button onClick={action} className="text-sm text-stone-600 hover:text-amber-700 transition-colors cursor-pointer block">{label}</button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-5">Rezeption</h3>
            <div className="space-y-4 text-sm text-stone-600 mb-6">
              <div className="flex items-baseline justify-between">
                <span className="text-stone-800 font-medium">Mo – Fr</span>
                <span>7 – 12 & 15 – 22 Uhr</span>
              </div>
              <div className="h-px bg-stone-900/10" />
              <div className="flex items-baseline justify-between">
                <span className="text-stone-800 font-medium">Sa, So & Feiertage</span>
                <span>8 – 12 & 15 – 22 Uhr</span>
              </div>
            </div>
            <button
              onClick={() => scrollToSection('kontakt')}
              className="inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm font-medium px-5 py-2.5 rounded-md transition-all duration-200 cursor-pointer"
            >
              <i className="ri-calendar-check-line text-base" />
              Zimmer anfragen
            </button>
          </div>

          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-5">Kontakt</h3>
            <div className="space-y-4 text-sm text-stone-600">
              <p className="whitespace-pre-line leading-relaxed text-stone-700">{t('footerAddress')}</p>
              <div className="h-px bg-stone-900/10" />
              <div className="space-y-2">
                <a href="tel:+498985642910" className="flex items-center gap-3 text-stone-700 hover:text-amber-700 transition-colors">
                  <i className="ri-phone-line text-amber-700 text-base"></i>
                  {t('footerPhone')}
                </a>
                <a href="mailto:hotel@alpenhof-gauting.de" className="flex items-center gap-3 text-stone-700 hover:text-amber-700 transition-colors">
                  <i className="ri-mail-line text-amber-700 text-base"></i>
                  hotel@alpenhof-gauting.de
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-stone-900/10 bg-white/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
            <p className="text-xs text-stone-500 text-center sm:text-left">{t('footerCopyright')}</p>
            <div className="flex items-center gap-1">
              {legalLinks.map((item, i, arr) => (
                <span key={item.label} className="flex items-center gap-1">
                  <button onClick={() => { navigate(item.path); window.scrollTo({ top: 0, behavior: 'instant' }); }} className="text-xs text-stone-500 hover:text-amber-700 transition-colors cursor-pointer whitespace-nowrap">{item.label}</button>
                  {i < arr.length - 1 && <span className="text-stone-400 text-xs">·</span>}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ============================================================ */
/* Preview-Seite: Slider mit fünf Varianten                      */
/* ============================================================ */
const VARIANTS = [
  {
    title: 'Dekorativer Bild-Streifen + heller Cremeton',
    subtitle: 'Bild als schmaler Banner oben, Footer-Inhalt hell auf stone-50 mit weißen Karten',
    Component: FooterVariant1,
  },
  {
    title: 'Cremeton ohne Bild',
    subtitle: 'Warmer Amber-50 Hintergrund, klare Spalten statt Karten, dezenter Amber-Strich oben',
    Component: FooterVariant2,
  },
  {
    title: 'Bild mit weißem Overlay (aquarell-artig)',
    subtitle: 'Bild bleibt als Atmosphäre erhalten, weißes Overlay (60%) entschärft, weiße Karten',
    Component: FooterVariant3,
  },
  {
    title: 'Amber Brand-Statement',
    subtitle: 'Kräftiger Amber-700-Footer wie Erweiterung des CtaBanner – mit serifer Headline, Signatur des Hauses',
    Component: FooterVariant4,
  },
  {
    title: 'Editorial mit Ausflugs-Bildcollage',
    subtitle: 'Heller Cremeton mit „Ausflüge ringsum"-Bildgrid als Teaser zur Ausflugsseite',
    Component: FooterVariant5,
  },
];

export default function FooterPreviewPage() {
  const [index, setIndex] = useState(0);
  const total = VARIANTS.length;
  const { Component, title, subtitle } = VARIANTS[index];

  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Sticky-Slider-Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-stone-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-baseline gap-3 min-w-0">
              <span className="text-3xl font-serif text-amber-700 shrink-0">{index + 1}</span>
              <span className="text-xs text-stone-400 shrink-0">/ {total}</span>
              <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-semibold text-stone-800 truncate">{title}</h2>
                <p className="text-xs text-stone-500 truncate">{subtitle}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={prev}
                className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-stone-300 text-stone-700 hover:bg-stone-100 hover:border-amber-700 hover:text-amber-700 transition-colors cursor-pointer"
                aria-label="Vorherige Variante"
              >
                <i className="ri-arrow-left-s-line text-xl" />
              </button>
              <button
                onClick={next}
                className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-stone-300 text-stone-700 hover:bg-stone-100 hover:border-amber-700 hover:text-amber-700 transition-colors cursor-pointer"
                aria-label="Nächste Variante"
              >
                <i className="ri-arrow-right-s-line text-xl" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-3">
            {VARIANTS.map((v, i) => (
              <button
                key={v.title}
                onClick={() => setIndex(i)}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                  i === index
                    ? 'bg-amber-700 text-white'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                Variante {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Inhalt-Platzhalter, damit der Footer wie im echten Layout wirkt */}
      <div className="flex-1 bg-gradient-to-b from-white to-stone-50 flex items-center justify-center px-6 py-20">
        <div className="max-w-xl text-center">
          <h1 className="text-2xl sm:text-3xl font-serif text-stone-800 mb-3">Footer-Vorschau</h1>
          <p className="text-sm text-stone-500 leading-relaxed">
            Scroll nach unten, um den Footer in dieser Variante zu sehen. Wechsle oben zwischen den drei Vorschlägen.
          </p>
        </div>
      </div>

      <Component />
    </div>
  );
}
